import { useEffect, useMemo, useCallback, useSyncExternalStore } from 'react';
import { useGetList } from 'react-admin';
import { useSocketIO } from '../../providers/SocketIOProvider';
import { phaseOf, PHASES, TERMINAL_PHASES, type Phase } from './phases';
import { useEffectiveStore, storeFilterFor, NO_STORE } from '../../context/StoreScopeContext';
import { getStoreIds, subscribeStoreIds } from '../../components/storeIdsStore';
import type { AdminOrder } from './types';

const EMPTY_BY_PHASE = (): Record<Phase, AdminOrder[]> =>
  Object.fromEntries(PHASES.map((p) => [p.key, [] as AdminOrder[]])) as Record<Phase, AdminOrder[]>;

/**
 * How far back the finished columns reach.
 *
 * Delivered and Cancelled are the only phases nothing ever leaves, so with a
 * 200-order fetch they otherwise fill with history and push New / Packing off
 * the side of the board. A rolling window rather than "since midnight", so a
 * late trading night doesn't blank its own history at 00:00.
 */
export const TERMINAL_WINDOW_HOURS = 12;

/**
 * Safety-net poll, used only while the socket is down.
 *
 * The board is push-driven (see onNewOrder below) and react-query's own
 * focus/reconnect refetches are deliberately off, so a dropped socket used to
 * mean a board that sat still and looked simply quiet. This keeps it moving at
 * a rate that's cheap but not embarrassing to watch.
 */
const DISCONNECTED_POLL_MS = 60_000;

/** Deeliver app orders only (exclude Uber Eats / Shopify channels) for v1. */
function isDeeliver(o: AdminOrder): boolean {
  return !(/uber|shopify/i.test(o.channel || o.source || '') || !!o.uberDirectId);
}

/** Every active store the API knows about, kept fresh by the data provider. */
function useKnownStoreIds(): string[] {
  return useSyncExternalStore(subscribeStoreIds, getStoreIds);
}

export function useLiveOrders() {
  // The branch filter is the app-bar picker now — no page-local selector. It
  // holds the store's id (externalId); a pick that's no longer one of the
  // user's stores falls back to "Select" (see resolveStore).
  const store = useEffectiveStore();
  // Nothing is fetched until a store (or "All stores") is chosen — the page shows
  // a "please select a store" prompt instead of a blank or demo board.
  const needsStore = store === NO_STORE;

  const { isConnected, subscribeAllOrders, onNewOrder } = useSocketIO();

  const { data, isLoading, refetch } = useGetList<AdminOrder>(
    'orders',
    {
      // The admin orders schema caps limit at 100 (admin/orders/schemas.js) — asking
      // for 200 returns 400.
      pagination: { page: 1, perPage: 100 },
      sort: { field: 'createdAt', order: 'DESC' },
      // Filter server-side by store id, so the 100 most recent are this store's
      // 100 — not whatever of this store happened to be in the global 100.
      filter: { storeId: storeFilterFor(store) },
    },
    {
      // Don't hit the API until the user has picked a store scope.
      enabled: !needsStore,
      // The board is pushed to over Socket.IO — a new order calls refetch() below.
      // Without these, react-query also refetches every time the tab regains focus
      // or the network blips, which is why the page looked like it was constantly
      // reloading even while sitting still.
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      // ...but with the socket down there's nothing left to push, so fall back to
      // a slow poll rather than showing a frozen board as though it were calm.
      refetchInterval: !needsStore && !isConnected ? DISCONNECTED_POLL_MS : false,
    },
  );

  // Subscribe to every active store, not just the ones that happen to appear in
  // the current page of orders. Keying off the data alone meant a branch with
  // nothing on the board was never subscribed to — so the order you most want to
  // hear about, the first of the day at a quiet store, was the one that didn't
  // push. The id list rides along on the orders response (see fetchApiData).
  const knownStoreIds = useKnownStoreIds();
  const storeKey = useMemo(
    () => Array.from(new Set([
      ...knownStoreIds,
      ...((data ?? []).map((o) => o.storeId).filter(Boolean) as string[]),
    ])).sort().join(','),
    [knownStoreIds, data],
  );

  useEffect(() => {
    if (!isConnected || !storeKey) return;
    subscribeAllOrders(storeKey.split(','));
  }, [isConnected, storeKey, subscribeAllOrders]);

  // A new order pushed over the socket is the thing that should refresh the board.
  useEffect(() => onNewOrder(() => { refetch(); }), [onNewOrder, refetch]);

  const orders = useMemo(() => (data || []).filter(isDeeliver), [data]);

  // Drop cancelled orders, then age out the finished columns. Both applied here
  // rather than inside byPhase so the header count and the board agree on what
  // "on the board" means.
  const filtered = useMemo(() => {
    const cutoff = Date.now() - TERMINAL_WINDOW_HOURS * 3600_000;
    return orders.filter((o) => {
      const phase = phaseOf(o.status);
      // A cancelled order needs nothing done to it — it has no place on a board
      // of work in flight. (See BOARD_PHASES, which drops the column to match.)
      if (phase === 'cancelled') return false;
      if (!TERMINAL_PHASES.includes(phase)) return true;
      // Fall back to placement time for anything finished without a stamp, so an
      // old order can't sit on the board forever on a missing deliveredAt.
      const finishedAt = o.deliveredAt || o.createdAt;
      return !finishedAt || new Date(finishedAt).getTime() >= cutoff;
    });
  }, [orders]);

  const byPhase = useMemo(() => {
    const map = EMPTY_BY_PHASE();
    filtered.forEach((o) => map[phaseOf(o.status)].push(o));
    return map;
  }, [filtered]);

  const stableRefetch = useCallback(() => { refetch(); }, [refetch]);

  return { orders: filtered, byPhase, isLoading, needsStore, isConnected, refetch: stableRefetch };
}
